<template>
  <div id="app">
    <Header
      v-if="!isAuthPage"
      :company="config.company"
      @toggleConfig="toggleConfigPanel"
    />
    <main class="main-content" :class="{ 'full-page': isAuthPage }">
      <router-view :config="config" />
    </main>
    <Footer v-if="!isAuthPage" :footer="config.footer" @openConfig="openConfigPanel" />
    
    <!-- 配置面板 -->
    <div class="config-overlay" :class="{ active: showConfigPanel }" @click="toggleConfigPanel"></div>
    <div class="config-panel" :class="{ active: showConfigPanel }">
      <div class="config-panel-header">
        <h3>网站配置管理</h3>
        <button class="config-panel-close" @click="toggleConfigPanel">&times;</button>
      </div>
      <div class="config-panel-body">
        <div class="config-tabs">
          <span 
            v-for="tab in tabs" 
            :key="tab.key"
            class="config-tab"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.name }}
          </span>
        </div>
        
        <!-- 基础信息 -->
        <div class="config-form" :class="{ active: activeTab === 'company' }">
          <div class="form-group">
            <label>企业名称</label>
            <input type="text" v-model="editConfig.company.name" />
          </div>
          <div class="form-group">
            <label>Logo地址</label>
            <div class="flex flex-center" style="gap: 15px;">
              <input type="text" v-model="editConfig.company.logo" />
              <img :src="editConfig.company.logo" class="image-preview" @error="handleImageError" />
            </div>
          </div>
          <div class="form-group">
            <label>口号标语</label>
            <input type="text" v-model="editConfig.company.slogan" />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>地址</label>
              <input type="text" v-model="editConfig.company.address" />
            </div>
            <div class="form-group">
              <label>电话</label>
              <input type="text" v-model="editConfig.company.phone" />
            </div>
          </div>
          <div class="form-group">
            <label>邮箱</label>
            <input type="email" v-model="editConfig.company.email" />
          </div>
        </div>
        
        <!-- 首页配置 -->
        <div class="config-form" :class="{ active: activeTab === 'home' }">
          <div class="form-group">
            <label>Banner 1 标题</label>
            <input type="text" v-model="editConfig.home.banners[0].title" />
          </div>
          <div class="form-group">
            <label>Banner 1 图片地址</label>
            <input type="text" v-model="editConfig.home.banners[0].image" />
          </div>
          <div class="form-group">
            <label>Banner 2 标题</label>
            <input type="text" v-model="editConfig.home.banners[1].title" />
          </div>
          <div class="form-group">
            <label>Banner 2 图片地址</label>
            <input type="text" v-model="editConfig.home.banners[1].image" />
          </div>
          <div class="form-group">
            <label>Banner 3 标题</label>
            <input type="text" v-model="editConfig.home.banners[2].title" />
          </div>
          <div class="form-group">
            <label>Banner 3 图片地址</label>
            <input type="text" v-model="editConfig.home.banners[2].image" />
          </div>
        </div>
        
        <!-- 数字乐园 -->
        <div class="config-form" :class="{ active: activeTab === 'products' }">
          <div class="form-group">
            <label>探索内容（JSON格式）</label>
            <textarea v-model="productsJson" rows="35" style="font-size: 12px; font-family: monospace;"></textarea>
          </div>
        </div>

        <!-- 探索方式 -->
        <div class="config-form" :class="{ active: activeTab === 'solutions' }">
          <div class="form-group">
            <label>探索方式（JSON格式）</label>
            <textarea v-model="solutionsJson" rows="35" style="font-size: 12px; font-family: monospace;"></textarea>
          </div>
        </div>
        
        <!-- 联系我们 -->
        <div class="config-form" :class="{ active: activeTab === 'contact' }">
          <div class="form-group">
            <label>地址</label>
            <input type="text" v-model="editConfig.contact.address" />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>固定电话</label>
              <input type="text" v-model="editConfig.contact.phone" />
            </div>
            <div class="form-group">
              <label>手机号码</label>
              <input type="text" v-model="editConfig.contact.mobile" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>微信号</label>
              <input type="text" v-model="editConfig.contact.wechat" />
            </div>
            <div class="form-group">
              <label>邮箱</label>
              <input type="email" v-model="editConfig.contact.email" />
            </div>
          </div>
        </div>
        
        <!-- 在线留言 -->
        <div class="config-form" :class="{ active: activeTab === 'messages' }">
          <OnlineMessages v-if="activeTab === 'messages'" />
        </div>

        <!-- 备案信息 -->
        <div class="config-form" :class="{ active: activeTab === 'footer' }">
          <div class="form-group">
            <label>版权信息</label>
            <input type="text" v-model="editConfig.footer.copyright" />
          </div>
          <div class="form-group">
            <label>备案号</label>
            <input type="text" v-model="editConfig.footer.icp" />
          </div>
        </div>
        
        <div class="form-actions" v-show="activeTab !== 'messages'">
          <button class="btn-save" @click="saveConfig">保存配置</button>
          <button class="btn-reset" @click="resetConfig">恢复默认</button>
        </div>
        <div class="form-actions" v-show="activeTab !== 'messages'">
          <button class="btn-export" @click="exportConfig">导出配置</button>
          <button class="btn-import" @click="triggerImport">导入配置</button>
          <input type="file" id="importFile" accept=".json" style="display: none;" @change="importConfig" />
        </div>
      </div>
    </div>
    
    <!-- Toast -->
    <div class="toast" :class="[toast.type, { show: toast.show }]">{{ toast.message }}</div>
    
    <!-- 密码弹窗 -->
    <div class="password-modal" :class="{ active: showPasswordModal }" @click.self="closePasswordModal">
      <div class="password-modal-content">
        <svg class="password-close" @click="closePasswordModal" viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
        <h4>请输入访问密码</h4>
        <div class="password-error" :class="{ show: passwordError }">密码错误</div>
        <input type="password" v-model="passwordInput" placeholder="请输入密码" @keyup.enter="verifyPassword" />
        <button @click="verifyPassword">确认</button>
      </div>
    </div>
  </div>
</template>

<script>
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'
import OnlineMessages from './components/admin/OnlineMessages.vue'
import configManager from './utils/configManager'

export default {
  name: 'App',
  components: {
    Header,
    Footer,
    OnlineMessages
  },
  data() {
    return {
      config: {},
      showConfigPanel: false,
      showPasswordModal: false,
      passwordInput: '',
      passwordError: false,
      CONFIG_PASSWORD: 'dengjiahui',
      activeTab: 'company',
      tabs: [
        { key: 'company', name: '基础信息' },
        { key: 'home', name: '首页' },
        { key: 'products', name: '数字乐园' },
        { key: 'solutions', name: '探索方式' },
        { key: 'contact', name: '联系' },
        { key: 'messages', name: '在线留言' },
        { key: 'footer', name: '备案' }
      ],
      editConfig: {},
      toast: {
        show: false,
        message: '',
        type: ''
      }
    }
  },
  computed: {
    isAuthPage() {
      return this.$route.meta && this.$route.meta.hideHeaderFooter
    },
    productsJson: {
      get() {
        return JSON.stringify(this.editConfig.products, null, 2)
      },
      set(val) {
        try {
          this.editConfig.products = JSON.parse(val)
        } catch (e) {
          this.showToast('JSON格式错误', 'error')
        }
      }
    },
    solutionsJson: {
      get() {
        return JSON.stringify(this.editConfig.solutions, null, 2)
      },
      set(val) {
        try {
          this.editConfig.solutions = JSON.parse(val)
        } catch (e) {
          this.showToast('JSON格式错误', 'error')
        }
      }
    }
  },
  created() {
    this.loadConfig()
  },
  methods: {
    loadConfig() {
      this.config = configManager.loadConfig()
      this.editConfig = JSON.parse(JSON.stringify(this.config))
    },
    toggleConfigPanel() {
      if (this.showConfigPanel) {
        this.showConfigPanel = false
        this.showPasswordModal = false
      } else {
        this.showPasswordModal = true
        this.passwordInput = ''
        this.passwordError = false
      }
    },
    openConfigPanel() {
      this.showPasswordModal = true
      this.passwordInput = ''
      this.passwordError = false
    },
    closePasswordModal() {
      this.showPasswordModal = false
      this.passwordInput = ''
      this.passwordError = false
    },
    verifyPassword() {
      if (this.passwordInput === this.CONFIG_PASSWORD) {
        this.showPasswordModal = false
        this.showConfigPanel = true
        this.editConfig = JSON.parse(JSON.stringify(this.config))
      } else {
        this.passwordError = true
        setTimeout(() => {
          this.passwordError = false
        }, 3000)
      }
    },
    saveConfig() {
      if (configManager.saveConfig(this.editConfig)) {
        this.config = JSON.parse(JSON.stringify(this.editConfig))
        this.showToast('保存成功', 'success')
      } else {
        this.showToast('保存失败', 'error')
      }
    },
    resetConfig() {
      if (confirm('确定要恢复默认配置吗？所有修改将被清除。')) {
        this.config = configManager.resetConfig()
        this.editConfig = JSON.parse(JSON.stringify(this.config))
        this.showToast('已恢复默认', 'success')
      }
    },
    exportConfig() {
      const data = configManager.exportConfig()
      const blob = new Blob([data], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'shuyu_config.json'
      a.click()
      URL.revokeObjectURL(url)
      this.showToast('导出成功', 'success')
    },
    triggerImport() {
      document.getElementById('importFile').click()
    },
    importConfig(e) {
      const file = e.target.files[0]
      if (!file) return
      
      const reader = new FileReader()
      reader.onload = (event) => {
        if (configManager.importConfig(event.target.result)) {
          this.loadConfig()
          this.showToast('导入成功', 'success')
        } else {
          this.showToast('导入失败，请检查JSON格式', 'error')
        }
      }
      reader.readAsText(file)
      e.target.value = ''
    },
    showToast(message, type = '') {
      this.toast.message = message
      this.toast.type = type
      this.toast.show = true
      setTimeout(() => {
        this.toast.show = false
      }, 2000)
    },
    handleImageError(e) {
      e.target.src = 'https://via.placeholder.com/80x80?text=图片'
    }
  }
}
</script>

<style scoped>
.main-content.full-page {
  padding: 0;
  min-height: 100vh;
}
</style>
